import React, { useMemo, useState } from 'react'
import { Box, Menu, CircularProgress, Select, MenuItem, Checkbox, ListItemText } from '@mui/material'
import { useQuery } from 'react-query'
import AdxButton from './AdxButton'
import AdxDatePickerV2 from './AdxDatePickerV2'
import { ReactComponent as ExportIcon } from 'assets/icons/svg/ArrowUpOnSquare.svg'
import { ReactComponent as CalendarIcon } from 'assets/icons/svg/Calendar.svg'
import { format, startOfMonth, endOfMonth } from 'date-fns'
import { profileDepartments } from 'services/api/dropdown'

const AdxExportButton = ({ 
  onExport, 
  loading = false, 
  disabled = false,
  variant = 'outlined',
  size = 'medium',
  sx = {},
  withDepartmentFilter = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [exportType, setExportType] = useState('monthly')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [startDate, setStartDate] = useState(startOfMonth(new Date()))
  const [endDate, setEndDate] = useState(endOfMonth(new Date()))
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([])

  const open = Boolean(anchorEl)

  const { data: departmentData = [], isLoading: departmentLoading } = useQuery(
    ['exportDepartmentList'],
    async () => {
      const { data } = await profileDepartments()
      return data || []
    },
    {
      enabled: withDepartmentFilter,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  )

  const departmentNameById = useMemo(() => {
    return departmentData.reduce((acc, department) => {
      acc[department.id] = department.name
      return acc
    }, {})
  }, [departmentData])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleExportTypeChange = (event) => {
    setExportType(event.target.value)
  }

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value)
  }

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  const handleDepartmentChange = (event) => {
    setSelectedDepartmentIds(event.target.value)
  }

  const renderSelectedDepartments = (selected) => {
    if (!selected?.length) return 'All Departments'
    if (selected.length === 1) return departmentNameById[selected[0]] || '1 Department'
    return `${selected.length} Departments`
  }

  const handleExport = () => {
    let exportData = {
      export_type: exportType
    }

    if (exportType === 'monthly') {
      exportData.month = selectedMonth
      exportData.year = selectedYear
    } else {
      exportData.start_date = format(startDate, 'yyyy-MM-dd')
      exportData.end_date = format(endDate, 'yyyy-MM-dd')
    }

    if (withDepartmentFilter) {
      exportData.department_ids = selectedDepartmentIds
    }

    onExport(exportData)
    handleClose()
  }

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ]

  const years = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - i
    return { value: year, label: year.toString() }
  })

  return (
    <>
      <AdxButton
        onClick={handleClick}
        disabled={disabled || loading}
        variant={variant}
        size={size}
        sx={{
          gap: 1,
          ...sx
        }}
      >
        {loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <ExportIcon width={16} height={16} />
        )}
        Export to Excel
      </AdxButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 300,
            p: 2
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarIcon width={16} height={16} />
              <Box sx={{ fontWeight: 'medium' }}>Export Options</Box>
            </Box>
            
            <Select
              value={exportType}
              onChange={handleExportTypeChange}
              sx={{ width: '100%', mb: 2 }}
            >
              <MenuItem value="monthly">Monthly Export</MenuItem>
              <MenuItem value="date_range">Date Range Export</MenuItem>
            </Select>

            {exportType === 'monthly' ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  sx={{ flex: 1 }}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  value={selectedYear}
                  onChange={handleYearChange}
                  sx={{ flex: 1 }}
                >
                  {years.map((year) => (
                    <MenuItem key={year.value} value={year.value}>
                      {year.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <AdxDatePickerV2
                  placeholder="Start Date"
                  selectedDate={startDate}
                  setSelectedDate={setStartDate}
                />
                <AdxDatePickerV2
                  placeholder="End Date"
                  selectedDate={endDate}
                  setSelectedDate={setEndDate}
                />
              </Box>
            )}

            {withDepartmentFilter && (
              <Select
                multiple
                displayEmpty
                value={selectedDepartmentIds}
                onChange={handleDepartmentChange}
                renderValue={renderSelectedDepartments}
                sx={{ width: '100%', mt: 2 }}
              >
                <MenuItem value="" disabled>
                  {departmentLoading ? 'Loading departments...' : 'Select departments'}
                </MenuItem>
                {departmentData.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    <Checkbox checked={selectedDepartmentIds.indexOf(department.id) > -1} />
                    <ListItemText primary={department.name} />
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <AdxButton
              variant="outlined"
              onClick={handleClose}
              size="small"
            >
              Cancel
            </AdxButton>
            <AdxButton
              onClick={handleExport}
              size="small"
              disabled={loading}
            >
              {loading ? 'Exporting...' : 'Export'}
            </AdxButton>
          </Box>
        </Box>
      </Menu>
    </>
  )
}

export default AdxExportButton
